import paths from 'constants/paths';
import { BACKEND_URL } from 'env';
import { Logout } from 'redux/slices/authSlice';
import { showToastr } from 'redux/slices/toastrSlice';
import { ODINFORGE_URL } from 'utils/api/odinForge';
import { getCookie, setCookie } from 'utils/helper/helper';
import axios from 'axios';

export const getHandlerKey = (url) => (url ? new URL(url).hostname : undefined);

const GENERIC_ERROR_MESSAGE = 'An unexpected error occurred. Please try again or contact support if the issue persists.';

let isRefreshing = false;
let refreshQueue = [];
const enqueue = (resolver) => refreshQueue.push(resolver);
const flushQueue = (token, err) => {
  refreshQueue.forEach((resolve) => resolve({ token, err }));
  refreshQueue = [];
};

const refreshToken = async () => {
  try {
    const refreshResponse = await axios({
      method: 'post',
      url: `${BACKEND_URL}platform/auth/refresh`,
      withCredentials: true,
      headers: {
        Accept: 'application/json',
        'Cache-Control': 'no-cache',
      },
    });

    if (refreshResponse.status === 200 && refreshResponse.data?.token) {
      setCookie('token', refreshResponse.data.token);
      return refreshResponse.data.token;
    }
    return null;
  } catch (error) {
    console.error('Error refreshing token:', error);
    return null;
  }
};

export const RequestHandlers = {
  [getHandlerKey(BACKEND_URL)]: {
    onFulfilled: (request) => {
      const token = getCookie('token');
      if (token && !request.headers['Authorization']) {
        request.headers['Authorization'] = token;
      }
      return request;
    },
  },

  Default: {
    onFulfilled: (request) => request,
    onRejected: (error) => {
      if (axios.isCancel(error)) {
        return Promise.reject(error);
      }
      console.error(error);
    },
  },
};

export const ResponseHandlers = {
  [getHandlerKey(BACKEND_URL)]: {
    onFulfilled: (response, dispatch) => {
      const isSilentSuccess = !!response?.config?.meta?.silentSuccess;
      if (
        !isSilentSuccess &&
    (response.status === 200 || response.status === 201) &&
    response.data?.meta?.message !== undefined &&
    response.data?.meta?.message !== null
      ) {
        dispatch(showToastr({ message: response.data?.meta?.message, type: 'success' }));
      }
      return response;
    },

    onRejected: async (error, dispatch) => {
      const status = error?.response?.status;
      const backendError = error?.response?.data?.error ?? error?.response?.data?.message;
      const isSignatureExpired = typeof backendError === 'string' && backendError.includes('Signature has expired');
      const originalRequest = error.config || {};

      if (status === 404) {
        window.location.href = '/error';
        return;
      }

      const isAuthStale = status === 401 || status === 419 || isSignatureExpired;

      const isCheckLoginApi = ['https://api-bi.swisscrm.com/api/user/checkLogin', 'https://reports-api.swisspriv.com/api/user/checkLogin'].includes(originalRequest?.url);

      if (isAuthStale && !isCheckLoginApi) {
        if (originalRequest.__isRetry) {
          dispatch(Logout());
          window.location.href = paths.login;
          return;
        } else {
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              enqueue(({ token, err }) => {
                if (err || !token) {
                  dispatch(Logout());
                  window.location.href = paths.login;
                  reject(error);
                  return;
                }
                try {
                  originalRequest.__isRetry = true;
                  originalRequest.headers = originalRequest.headers || {};
                  originalRequest.headers['Authorization'] = token;
                  resolve(axios(originalRequest));
                } catch (e) {
                  reject(e);
                }
              });
            });
          }

          isRefreshing = true;
          try {
            const newToken = await refreshToken();
            isRefreshing = false;
            flushQueue(newToken, null);

            if (newToken) {
              originalRequest.__isRetry = true;
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers['Authorization'] = newToken;
              return axios(originalRequest);
            }
          } catch (refreshErr) {
            isRefreshing = false;
            flushQueue(null, refreshErr);
          }

          dispatch(Logout());
          window.location.href = paths.login;
          return;
        }
      }

      if (status === 403) {
        return error.response;
      }

      if (!isAuthStale) {
        const messageToShow = typeof status === 'number' && status >= 500 ? GENERIC_ERROR_MESSAGE : backendError ?? error.message;

        dispatch(
          showToastr({
            message: messageToShow,
            type: 'error',
          })
        );
      }

      throw error;
    },
  },

  [getHandlerKey(ODINFORGE_URL)]: {
    onFulfilled: (response) => {
      return response;
    },

    onRejected: (error, dispatch) => {
      dispatch(
        showToastr({
          message: `OdinForge: ${error.response?.data?.status ?? 'Error'}`,
          type: 'error',
        })
      );
    },
  },

  Default: {
    onFulfilled: (response) => response,
    onRejected: (error) => console.error(error),
  },
};
